import React, { useState, useRef, useEffect } from "react";
import { ReactGridLayout, WidthProvider } from "react-grid-layout/legacy";
import { Box, Typography, Paper, IconButton, Tooltip } from "@mui/material";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import IframeCard from "./IframeCard";

import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";

const MainGridLayout = WidthProvider(ReactGridLayout);

const IframeCardsGrid = ({ items = [], layout: initialLayout, onLayoutChange }) => {
  const [maximizedId, setMaximizedId] = useState(null);
  const containerRef = useRef(null);
  const [containerDimensions, setContainerDimensions] = useState({ width: 0, height: 0 });

  useEffect(() => {
    if (!containerRef.current) return;

    const observer = new ResizeObserver((entries) => {
      for (let entry of entries) {
        setContainerDimensions({
          width: entry.contentRect.width,
          height: entry.contentRect.height
        });
      }
    });

    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, [maximizedId]);

  const handleMaximize = (id) => {
    setMaximizedId(prev => (prev === id ? null : id));
  };

  const count = items.length;
  const colsCount = count <= 1 ? 1 : count <= 2 ? 2 : count <= 4 ? 2 : count <= 6 ? 3 : count <= 9 ? 3 : 4;
  const rowsCount = Math.ceil(count / colsCount);

  const wUnits = 12 / colsCount;
  const hUnits = 6;

  const defaultLayout = items.map((item, index) => ({
    i: item.id.toString(),
    x: (index % colsCount) * wUnits,
    y: Math.floor(index / colsCount) * hUnits,
    w: wUnits,
    h: hUnits,
  }));

  const currentLayout = initialLayout || defaultLayout;

  const margin = 2;
  const { height: containerHeight } = containerDimensions;

  const totalHUnits = rowsCount * hUnits;

  const calculatedRowHeight = containerHeight > 0
    ? (containerHeight - (totalHUnits + 1) * margin) / totalHUnits
    : 30;

  const maximizedItem = items.find((item) => item.id.toString() === maximizedId);

  return (
    <Box
      ref={containerRef}
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        position: "relative",
        overflow: "hidden"
      }}
    >
      <Box
        sx={{
          flexGrow: 1,
          position: "relative",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <style>{`
          .react-grid-layout {
            height: 100% !important;
          }
          .maximized-grid-item {
            transform: none !important;
            width: 100% !important;
            height: 100% !important;
            z-index: 10 !important;
            top: 0 !important;
            left: 0 !important;
            background-color: var(--mui-palette-background-paper, #fff);
          }
          .hidden-grid-item {
            visibility: hidden !important;
            pointer-events: none !important;
          }
        `}</style>

        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", flexGrow: 1, width: "100%", minHeight: 0, position: "relative" }}>
          <Box sx={{ width: "100%", height: "100%", position: "relative" }}>
            <MainGridLayout
              className="layout"
              layout={currentLayout}
              cols={12}
              rowHeight={calculatedRowHeight}
              isDraggable={false}
              isResizable={false}
              margin={[margin, margin]}
              onLayoutChange={onLayoutChange}
              autoSize={false}
            >
              {items.map((item) => {
                const isMax = maximizedId === item.id.toString();
                const isHidden = maximizedId && !isMax;
                return (
                  <div 
                    key={item.id.toString()}
                    className={isMax ? "maximized-grid-item" : (isHidden ? "hidden-grid-item" : "")}
                  >
                    <IframeCard
                      title={item.title}
                      url={item.url}
                      isMaximized={isMax}
                      onMaximize={() => handleMaximize(item.id.toString())}
                    />
                  </div>
                );
              })}
            </MainGridLayout>
          </Box>
        </Box>

        {maximizedId && (
          <Paper
            elevation={3}
            sx={{
              mt: 0,
              mb: 4,
              p: 1.5,
              display: "flex", gap: 1, overflowX: "auto",
              backgroundColor: (theme) => theme.palette.mode === 'dark' ? "rgba(0,0,0,0.6)" : "rgba(255,255,255,0.9)",
              borderRadius: "12px",
              border: (theme) => `1px solid ${theme.palette.divider}`,
              zIndex: 20,
            }}
          >
            <Typography variant="caption" sx={{ alignSelf: "center", mr: 1, fontWeight: 700, color: "text.secondary", px: 1 }}>
              MINIMIZED:
            </Typography>
            {items.map((item) => (
              <Box
                key={item.id}
                onClick={() => setMaximizedId(item.id.toString())}
                sx={{
                  px: 2, py: 0.5, borderRadius: 2, cursor: "pointer", fontSize: "0.75rem", fontWeight: 600, whiteSpace: "nowrap",
                  backgroundColor: item.id.toString() === maximizedId ? "primary.main" : "background.paper",
                  color: item.id.toString() === maximizedId ? "primary.contrastText" : "text.secondary",
                  border: (theme) => `1px solid ${item.id.toString() === maximizedId ? theme.palette.primary.main : theme.palette.divider}`,
                  transition: "all 0.2s",
                  "&:hover": { backgroundColor: item.id.toString() === maximizedId ? "primary.dark" : "action.hover" },
                }}
              >
                {item.title}
              </Box>
            ))}
          </Paper>
        )}
      </Box>
    </Box>
  );
};

export default IframeCardsGrid;
